import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Grid, Box, Typography, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import DescriptionIcon from '@mui/icons-material/Description';

import { getPlannerInfluencersES } from 'api_handlers/es';
import InfluencerListV2 from './InfluencerListV2';
import Search from '../Search';
import AiInfluencerListV2 from './AiInfluencerListV2';
import DiscoverThemeProvider from './DiscoverThemeProvider';
import BriefAssistant from '../BriefAssistant';
import {
  addFilterInPayload,
  fetchInfluencersAction,
  updateSelectedInfluencers,
} from 'actions/planner2';
import { updateFilters } from 'actions/planner-filters';
import { FEATURE_BRIEF_ASSISTANT } from 'config';

import mixpanel from '../../../../utils/mixpanel';
import './discoverV2.css';

class InfluencerDiscoverV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: [],
      briefOpen: false,
      brief: '',
      aiMode: false,
      aiInfluencers: [],
      aiLoading: false,
      aiQuery: '',
      aiTotalResults: 0,
      platform: 'instagram',
      page: 1,
      isPlatformChange: false,
      sortBy: 'relevancy',
      sorting: 'true',
      enhancedSearch: false,
    };
  }

  updateFilters = ({ sortBy }) => {
    this.props.addFilterInPayload('sortBy', sortBy, 'true');
  };

  /**
   * Apply the filters the user approved in the brief drawer.
   *
   * Every dispatch passes requiredResultUpdate = false: updateFilters schedules
   * a search 200ms after any filter that passes true, so applying N filters that
   * way would fire N overlapping searches. One explicit fetch at the end instead.
   */
  applyBriefFilters = (filters, brief) => {
    Object.keys(filters).forEach((key) => {
      this.props.applyPlannerFilter(key, filters[key], false);
    });

    this.props.fetchInfluencersAction(1);

    this.setState({ briefOpen: false, brief, aiMode: false, page: 1 });

    if (process.env.NODE_ENV === 'production') {
      mixpanel.track('planner_discover', {
        action: 'brief_filters_applied',
        filter_count: Object.keys(filters).length,
      });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.page !== this.state.page) {
      this.fetchAiInfluencers(this.state.page, this.state.aiQuery);
    } else if (prevState.platform !== this.state.platform) {
      this.setState({ isPlatformChange: true });
    } else if (prevState.sortBy !== this.state.sortBy) {
      this.setState({ sorting: 'true' });
      this.setState({ sortBy: this.state.sortBy }, () => {
        this.fetchAiInfluencers(1, this.state.aiQuery, 'true', this.state.sortBy, 'desc');
      });
    }
  }

  fetchAiInfluencers = async (
    page,
    searchQuery = '',
    sorting = this.state.sorting,
    sort_by = this.state.sortBy,
    sort_order = 'desc',
  ) => {
    try {
      this.setState({ aiLoading: true });

      const payload = this.state.aiMode
        ? {
            'campaign-level-requirements': {
              'price-per-creator': 10000000000,
            },
            'creator-level-requirements': {
              platform: this.state.platform,
            },
            'advance-filters': {
              sortOrder: 'desc',
              sortBy: 'category',
            },
            query: searchQuery,
            aiSorting: sorting,
            ai_sort_by: sort_by,
            setReset: false,
            ...(this.state.enhancedSearch ? { 'ai-version': 'v2' } : {}),
          }
        : {
            'campaign-level-requirements': {
              'price-per-creator': 10000000000,
            },
            'creator-level-requirements': {
              platform: this.state.platform,
            },
            'advance-filters': {
              sortOrder: 'desc',
              sortBy: 'category',
            },
            setReset: false,
          };

      const response = await getPlannerInfluencersES(payload, page);
      const data = await response.json();

      const results = data?.results || [];
      const total = data?.count || results.length;
      if (this.state.aiMode && page === 1) {
        mixpanel.track('planner_discover', {
          action: 'ai_search_results',
          data: {
            platform: this.state.platform,
            result_count: total,
          },
        });
      }
      this.setState({
        aiInfluencers: results,
        aiTotalResults: total,
        aiLoading: false,
      });
    } catch (err) {
      console.error('Error fetching influencers:', err);
      if (this.state.aiMode) {
        mixpanel.track('planner_discover', {
          action: 'ai_search_error',
          data: { platform: this.state.platform },
        });
      }
      this.setState({ aiLoading: false });
    }
  };

  handleSearch = async (searchValue, enhancedSearch = false) => {
    await new Promise((resolve) => this.setState({ aiQuery: searchValue, enhancedSearch }, resolve));
    if (this.state.aiMode && searchValue.trim()) {
      await this.fetchAiInfluencers(1, searchValue, 'true', 'relevancy', 'desc');
    } else {
      await this.fetchInfluencers(1);
    }
    this.setState({ isPlatformChange: false });
  };

  render() {
    const { influencers, loading, totalResults } = this.props;
    const { aiMode } = this.state;
    const liveCount = aiMode
      ? this.state.isPlatformChange
        ? 0
        : this.state.aiTotalResults
      : totalResults;

    return (
      <DiscoverThemeProvider>
        <Box className="discover-v2-root" sx={{ pb: 2 }}>
          <Grid container spacing={1.5}>
            {/* ---------- Hero header ---------- */}
            <Grid item xs={12}>
              <Box className="dv2-hero">
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <p className="dv2-hero-kicker">Influencer Discovery</p>
                    <h1 className="dv2-hero-title">Discover the creators that fit your brand</h1>
                    <p className="dv2-hero-sub">
                      A curated index of vetted creators across Instagram &amp; YouTube.
                    </p>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box className="dv2-stat">
                      <div className="dv2-stat-num">
                        {liveCount != null ? parseInt(liveCount).toLocaleString('en-IN') : '—'}
                      </div>
                      <div className="dv2-stat-label">MATCHING CREATORS</div>
                    </Box>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 1,
                        borderRadius: '999px',
                        border: '1px solid rgba(255,255,255,0.16)',
                        background: aiMode ? 'rgba(254,189,28,0.16)' : 'rgba(255,255,255,0.08)',
                        color: aiMode ? '#febd1c' : 'rgba(255,255,255,0.85)',
                      }}
                    >
                      {aiMode ? (
                        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <TuneIcon sx={{ fontSize: 16 }} />
                      )}
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                        {aiMode ? 'AI Discovery' : 'Standard Filters'}
                      </Typography>
                    </Box>
                    {FEATURE_BRIEF_ASSISTANT && (
                      <Button
                        size="small"
                        startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                        onClick={() => this.setState({ briefOpen: true })}
                        sx={{
                          ml: 1,
                          px: 1.5,
                          py: 1,
                          borderRadius: '999px',
                          textTransform: 'none',
                          fontSize: 12.5,
                          fontWeight: 700,
                          border: '1px solid rgba(255,255,255,0.16)',
                          background: 'rgba(255,255,255,0.08)',
                          color: 'rgba(255,255,255,0.85)',
                          '&:hover': { background: 'rgba(255,255,255,0.16)' },
                        }}
                      >
                        From a brief
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* ---------- Filters (reuses the full Search component) ---------- */}
            <Grid item xs={12}>
              <Search
                v2
                aiMode={(value) => {
                  // Switching Manual <-> AI must not carry a stale selection
                  // (the action bar showed the previous tab's picks).
                  this.props.updateSelectedInfluencers(new Set());
                  this.setState({ aiMode: value });
                }}
                fetchInfluencers={this.fetchInfluencers}
                plannerFilter={async (val) => await this.setState({ filters: val })}
                onSearch={this.handleSearch}
                platform={(value) => this.setState({ platform: value })}
                aiSearchLoading={this.state.aiLoading}
              />
            </Grid>

            {/* ---------- AI mode helper banner ---------- */}
            {aiMode && (
              <Grid item xs={12}>
                <Box className="dv2-ai-banner">
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: '#c79309' }} />
                  <span>
                    <strong>AI Discovery is on.</strong> Describe who you’re looking for in plain
                    language (e.g. “fitness creators from South India with high engagement”) and we’ll
                    rank the best matches.
                  </span>
                </Box>
              </Grid>
            )}

            {/* ---------- Results ---------- */}
            <Grid item xs={12}>
              {!aiMode ? (
                <InfluencerListV2
                  isDiscover={true}
                  influencers={influencers}
                  loading={loading}
                  totalResults={totalResults}
                  useCategorySort={true}
                  filters={this.state.filters}
                />
              ) : (
                <AiInfluencerListV2
                  influencers={this.state.isPlatformChange ? [] : this.state.aiInfluencers}
                  loading={this.state.aiLoading}
                  totalResults={this.state.isPlatformChange ? 0 : this.state.aiTotalResults}
                  useCategorySort={true}
                  onSortChange={(val) => this.setState({ sortBy: val })}
                  onPageChange={(value) => this.setState({ page: value })}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        {FEATURE_BRIEF_ASSISTANT && (
          <BriefAssistant
            open={this.state.briefOpen}
            onClose={() => this.setState({ briefOpen: false })}
            onApplyFilters={this.applyBriefFilters}
            selection={this.props.selectedCreators}
          />
        )}
      </DiscoverThemeProvider>
    );
  }
}

const mapStateToProps = (state) => {
  // selectedInfluencers is a Set of ids, so resolve it back to the rows the
  // plan preview needs.
  const selectedIds = state.planner2.selectedInfluencers || new Set();
  const influencers = state.planner2.influencers || [];

  return {
    payload: state.planner2.payload,
    loading: state.planner2.loading,
    influencers,
    totalResults: state.planner2.totalInfluencersCount,
    selectedCreators: influencers.filter((influencer) => selectedIds.has(influencer.id)),
  };
};

const actionPlanner = {
  addFilterInPayload: addFilterInPayload,
  updateSelectedInfluencers: updateSelectedInfluencers,
  // Named to avoid shadowing this component's own updateFilters method.
  applyPlannerFilter: updateFilters,
  fetchInfluencersAction: fetchInfluencersAction,
};

export default connect(mapStateToProps, actionPlanner)(InfluencerDiscoverV2);
